// @ts-check
// =============================================================================
// js/screens/settings.js — content filters and sound.
//
// WHAT THIS IS (plan/settings.md; old RenderSettings :2559)
//   Bigger than it looks: saving settings makes the SERVER re-derive the
//   entire visible card/art collection ("Save Settings" returns newCards),
//   so every art-showing screen renders differently afterwards.
//
// PORTED BEHAVIOR (gameplay-content critical — kept verbatim)
//   - Censor toggles (nude/smut/vore/gore) display INVERTED: the image is
//     colorful when the content is ALLOWED (censor flag false).
//   - Gender options (4) cascade: toggling a base gender also sets its
//     SELF-pairing (male -> male_male) to the same value (old :2579).
//   - Forced minimums: at least one of ♂/♀ and one of the ♂♂/♂♀/♀♀/♀♂
//     pairings must stay enabled — violating combos snap back with a notice.
//   - SFX: mute toggle (🔈/🔉/🔊) + 0–100 volume slider.
//   (Renaming lives in the player profile popup now — ui/profile.js.)
// =============================================================================

import { store, notify, setSlice } from '../core/store.js';
import { request } from '../net/http.js';
import { refreshCardData } from '../render/card-tile.js';
import { clearImageCache } from '../render/image-cache.js';
import { toast } from '../ui/toast.js';
import { escapeHtml } from '../ui/modal.js';

/** Toggle images live on the public art bucket (same as the old client). */
const SETTINGS_ART_BASE = 'https://storage.googleapis.com/deckbiter/settings/';

/** The four base genders (cascade sources). */
const BASE_GENDERS = ['male', 'female', 'futa', 'gyno'];

/** @type {HTMLElement|null} */
let screenEl = null;

/**
 * Render the settings screen.
 * @param {HTMLElement} container
 * @param {Record<string,string>} _params
 * @returns {void}
 */
export function render(container, _params) {
	screenEl = document.createElement('div');
	screenEl.className = 'screen';
	container.appendChild(screenEl);
	paint();
}

/** Nothing persistent to tear down. */
export function destroy() {
	screenEl = null;
}

/** Repaint from current settings. */
function paint() {
	if (!screenEl || !store.userData) return;
	const settings = store.userData.settings;
	if (settings.sfx == null) settings.sfx = 100;

	let html = `<h1 class="screen-title">Settings and Options</h1>`;
	// (Renaming moved to the player profile popup — the 👤 chip in the nav.)

	// --- Censor toggles (image ON = content allowed = censor flag false) ----
	html += `<div class="settings-section">Censor Options</div><div class="settings-grid">`;
	for (const key of Object.keys(settings.censor)) {
		html += imageToggle(key, `censor/${key}`, !settings.censor[key]);
	}
	html += `</div>`;

	// --- Gender options + pairings ------------------------------------------
	html += `<div class="settings-section">Gender Options</div><div class="settings-grid">`;
	let i = 0;
	for (const key of Object.keys(settings.gender)) {
		html += imageToggle(key, `gender/${key}`, settings.gender[key]);
		if (i === 3) {
			html += `</div><div class="settings-section">Gender Pairings</div><div class="settings-grid">`;
		} else if ([7, 11, 15].includes(i)) {
			html += `</div><div class="settings-grid">`;
		}
		i++;
	}
	html += `</div>`;

	// --- Sound ---------------------------------------------------------------
	const speaker = settings.sfx === 0 ? '🔈' : settings.sfx < 25 ? '🔉' : '🔊';
	html += `
		<hr style="width:100%;opacity:0.3;">
		<div class="settings-section">Sound Options</div>
		<div class="settings-row">
			<button id="btn-sfx-toggle" class="settings-speaker">${speaker}</button>
			<input id="sfx-volume" type="range" min="0" max="100" value="${settings.sfx}" />
		</div><br><br>`;

	screenEl.innerHTML = html;
	wireEvents(settings);
}

/**
 * One round image toggle (old ImageToggleButton :2682): colorful when on,
 * grayscale + faded when off.
 * @param {string} key     The settings key (also the bucket image name).
 * @param {string} option  "group/key" click payload.
 * @param {boolean} value  Is the option currently on?
 * @returns {string} HTML
 */
function imageToggle(key, option, value) {
	return `
		<button class="settings-toggle" data-option="${option}">
			<img src="${SETTINGS_ART_BASE}${key}.png" alt="${escapeHtml(key)}"
				class="${value ? '' : 'is-off'}" loading="lazy">
		</button>`;
}

/**
 * Wire clicks; every change mutates settings + saves (old flow saved on every
 * toggle too).
 * @param {any} settings
 */
function wireEvents(settings) {
	if (!screenEl) return;

	for (const btn of screenEl.querySelectorAll('.settings-toggle')) {
		btn.addEventListener('click', () => {
			const [group, key] = String(btn.getAttribute('data-option')).split('/');
			settings[group][key] = !settings[group][key];

			// Base-gender cascade: the self-pairing follows (old :2579).
			if (group === 'gender' && BASE_GENDERS.includes(key)) {
				for (const pairKey in settings.gender) {
					if (('_' + pairKey).includes('_' + key + '_' + key)) {
						settings.gender[pairKey] = settings.gender[key];
					}
				}
			}
			enforceMinimums(settings);
			paint();
			void saveSettings(settings);
		});
	}

	screenEl.querySelector('#btn-sfx-toggle')?.addEventListener('click', () => {
		settings.sfx = settings.sfx > 0 ? 0 : 100;
		paint();
		void saveSettings(settings);
	});
	screenEl.querySelector('#sfx-volume')?.addEventListener('change', (e) => {
		settings.sfx = Math.max(0, Math.min(100, parseInt(/** @type {HTMLInputElement} */ (e.target).value, 10) || 0));
		paint();
		void saveSettings(settings);
	});

}

/**
 * The forced-minimum rules (old :2598): the game must have SOMETHING to show.
 * @param {any} settings
 */
function enforceMinimums(settings) {
	let note = '';
	if (
		!settings.gender.male_male && !settings.gender.male_female &&
		!settings.gender.female_female && !settings.gender.female_male
	) {
		settings.gender.female_female = true;
		settings.gender.male_female = true;
		settings.gender.female_male = true;
		settings.gender.male_male = true;
		note = 'Sorry, for now you\'ll need to have ♂♂, ♂♀, or ♀♀ pairings enabled.';
	}
	if (!settings.gender.male && !settings.gender.female) {
		settings.gender.male = true;
		settings.gender.female = true;
		note = 'Sorry, for now you\'ll need to have ♂ or ♀ characters enabled.';
	}
	if (note) toast(note, { error: true, ms: 5000 });
}

/**
 * Persist + swap in the re-derived collection (old SaveSettings :2672).
 * @param {any} settings
 */
async function saveSettings(settings) {
	const returned = await request('Save Settings', { newSettings: settings });
	if (!returned) {
		toast('Could not save settings.', { error: true });
		return;
	}
	// Guests have no DB row: their settings persist in the browser and get
	// replayed at the next login (session.js guestSettings).
	if (store.session.isGuest) {
		try {
			localStorage.setItem('guestSettings', JSON.stringify(settings));
		} catch {
			// private-mode quota — the choice just won't survive a reload
		}
	}
	if (returned.newCards) {
		// The whole visible collection was re-derived: swap it everywhere and
		// drop stale rendered card images.
		store.userData.cards = returned.newCards;
		setSlice('cards', returned.newCards);
		await refreshCardData();
		await clearImageCache();
		notify('userData');
	}
}
